/* eslint camelcase: 0 */

import { Api } from '@/service/Service'
import CheckoutConfig from '@/../PaymentIQCheckoutClientConfig'
import Store from '@/store'
import JavascriptAPI from '@/utils/JavascriptAPI'
import { inIframe } from '@/utils/helpers'
import { User } from '@/store/context/contextTypes'
import { DidUserdata } from '@/service/ApiResponseTypes'

/* DID Interfaces  */

interface DidVerificationType {
  authParameters: string
  codeVerifier: string
}

// PKCE HELPER FUNCTIONS
// Generate a secure random string using the browser crypto functions
function generateRandomString (): string {
  var array = new Uint32Array(28)
  window.crypto.getRandomValues(array)
  return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('')
}
// Calculate the SHA256 hash of the input text.
// Returns a promise that resolves to an ArrayBuffer
function sha256 (plain: string): PromiseLike<ArrayBuffer> {
  const encoder = new TextEncoder()
  const data = encoder.encode(plain)
  return window.crypto.subtle.digest('SHA-256', data)
}
// Base64-urlencodes the input string
export function base64urlencode (str: ArrayBuffer): string {
  // Convert the ArrayBuffer to string using Uint8 array to convert to what btoa accepts.
  // btoa accepts chars only within ascii 0-255 and base64 encodes them.
  // Then convert the base64 encoded to base64url encoded
  //   (replace + with -, replace / with _, trim trailing =)
  return btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
// Return the base64-urlencoded sha256 hash for the PKCE challenge
async function pkceChallengeFromVerifier (v: string): Promise<ArrayBuffer> {
  return sha256(v)
  // const hashed = await sha256(v)
  // return base64urlencode(hashed)
}
// authEndpoint is normal URL to start auth-flow, e.g. https://demo-api.gii.cloud/api/oauth/auth?client_id=bambora-demo&....

export async function createDidCodeVerification (): Promise<any> {
  const codeVerifier = generateRandomString()
  if (!inIframe()) {
    let codeChallenge = await pkceChallengeFromVerifier(codeVerifier)
    const pkceParameter = '&code_challenge_method=S256&code_challenge=' + codeChallenge
    // Mimic the behaviour when not running against integration-script
    postMessage({
      eventType: 'PIQ_LOOKUP_SHA_256',
      payload: {
        codeChallenge,
        codeVerifier
      }
    }, '*')
  } else {
    JavascriptAPI.__GENERATE_SHA_256__(codeVerifier)
  }
}

export function handleSHA256Response (codeChallenge: string, codeVerifier: string) {
  Store.dispatch('context/setDidInitialAuth', { codeVerifier, codeChallenge })
}

interface DIDProviderConfig {
  provider: string
  display?: string
  country: string,
  theme?: string
}

export function constructDIDProviderUrl (config: DIDProviderConfig): string {
  let environment = null
  let locale = null
  let didClientId = null
  if (process.env.NODE_ENV === 'test') {
    environment = 'development'
    locale = 'sv_SE'
    didClientId = 'bambora-demo'
  } else {
    environment = Store.state.context.config.environment
    locale = Store.state.context.config.locale
    didClientId = Store.state.context.config.didClientId
  }

  const { DID_CLIENT_PATH, DID_REDIRECT_URI } = CheckoutConfig[environment]
  let url = `${DID_CLIENT_PATH}/api/oauth/auth?`
  const parameters = {
    identity_provider: config.provider,
    country: config.country,
    display: config.display,
    redirect_method: 'postmessage',
    client_id: didClientId,
    redirect_uri: encodeURIComponent(DID_REDIRECT_URI),
    response_type: 'code',
    scope: 'openid',
    state: '123456789123',
    ui_locales: mapDidLocale(locale)
  }
  for (let key in parameters) {
    url += `${key}=${parameters[key]}&`
  }
  url = url.substring(0, url.length - 1) // remove last &

  if (config.theme) {
    url += `&ui_friendly=${config.theme}`
  }
  return url
}

interface DIDClaims {
  id_token: any
}

export function generateDidClaims (userData: User, country: string): DIDClaims {
  const claims: DIDClaims = {
    id_token: {
      given_name: {
        value: userData.firstName
      },
      family_name: {
        value: userData.lastName
      },
      street_address: {
        value: userData.address1
      },
      postal_code: {
        value: userData.zip
      },
      locality: {
        value: userData.city
      },
      country: {
        value: country
      },
      formatted: {
        value: `${userData.address1}\n${userData.zip} ${userData.city}`
      },
      email: {
        value: userData.email
      },
      phone_number: {
        value: userData.phonenumber
      }
    }
  }
  if (userData.nationalId) {
    claims.id_token.ssn = {
      value: userData.nationalId
    }
    if (country === 'DK') {
      claims.id_token.ssn_type = {
        value: 'CPR'
      }
    } else {
      claims.id_token.ssn_type = {
        value: 'personnummer'
      }
    }
  }
  return claims
}

export async function handleDIDRedirectResponse (message: any) {
  try {
    if (message.error) {
      // Store.dispatch('context/setError', { key: 'main', error: 'failed_did_authentication' })
      Store.dispatch('context/setIsLoading', false)
      console.error(message.error, message.error_description, message.error_hint)
    }
    const { DID_REDIRECT_URI } = CheckoutConfig[Store.state.context.config.environment]
    const DID_CLIENT_ID = Store.state.context.config.didClientId
    const codeVerifier = Store.state.context.didInitialAuth.didCodeVerifier
    const urlParams = new URLSearchParams()
    for (let [key, value] of Object.entries(message)) {
      urlParams.append(key, value as string)
    }
    const body = new URLSearchParams({
      code: message.code || '',
      client_id: DID_CLIENT_ID,
      code_verifier: codeVerifier,
      grant_type: 'authorization_code',
      redirect_uri: DID_REDIRECT_URI
    })
    const { data } = await Api.getDidUserInfo(body)
    const { access_token, id_token } = data
    Store.dispatch('context/setDidAuthTokens', { access_token, id_token })
  } catch (err) {
    // Store.dispatch('context/setError', { key: 'main', error: 'failed_did_authentication' })
    Store.dispatch('context/setIsLoading', false)
    Store.dispatch('context/continueNextStep') // show next input field in the wizard
    console.error(err)
    console.error('Failed to handle DID redirect response')
  }
}

export function mapFromDidUserData (data: any): User {
  const {
    ssn,
    address,
    given_name,
    family_name,
    email,
    phone_number,
    uuid
  } = data
  const { street_address, postal_code, region, locality, country } = address

  const mappedData: User = {
    userId: uuid,
    firstName: given_name,
    lastName: family_name,
    address1: street_address,
    address2: '',
    zip: postal_code,
    city: region || locality,
    country: country,
    email: email,
    phonenumber: phone_number
  }

  if (ssn) {
    mappedData.nationalId = ssn
  }

  return mappedData
}

export function mapToDidUserData (data: any): DidUserdata {
  const {
    firstName,
    lastName,
    email,
    phonenumber,
    address1,
    zip,
    city
  } = data
  const didData: DidUserdata = {
    given_name: firstName,
    family_name: lastName,
    address: {
      street_address: address1,
      postal_code: zip,
      region: city
    },
    email: email,
    phone_number: phonenumber
  }

  return didData
}

function mapDidLocale (locale) {
  const locales = {
    'sv_SE': 'sv-SE',
    'en_GB': 'en',
    'en_US': 'en',
    'no_NO': 'nb-NO',
    'nb_NO': 'no-NO',
    'da_DK': 'da-DK',
    'fi_FI': 'fi-FI'
  }
  return locales[locale]
}
